import { FC, useEffect, useRef } from "react";
import RoundedIcon from "../dashboard/RoundedIcon";
import { ChevronDown, MoveRight, Plus, Trash2 } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import CDropdown from "../CDropdown";
import JsonInputField from "./JsonInputField";
import JsonOutputField from "./JsonOuputField";

interface Output {
    status: string;
    body: object | string;   // can be an object or a string
    description: string;
}

interface Api {
    input: {
        body: object | string; // can be an object or a string
        description: string;
    };
    isApi: boolean;
    output: Output[];
}

interface ApisProps {
    apis: Api[];
    setApis: (apis: Api[]) => void;
    preview?: boolean;
}

const Apis: FC<ApisProps> = ({ apis, setApis, preview }) => {
    // Hold an array of refs (one per API row)
    const containerRefs = useRef<(HTMLDivElement | null)[]>([]);
    // Track previous length so we can scroll to the new row
    const prevApisLength = useRef(apis.length);

    useEffect(() => {
        if (apis.length > prevApisLength.current) {
            // New API added – scroll the last one into view
            const lastIndex = apis.length - 1;
            const node = containerRefs.current[lastIndex];
            if (node) {
                node.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }
        prevApisLength.current = apis.length;
    }, [apis]);




    return (
        <>
            {apis.map((api, index) => (
                <div
                    className="col-span-12 grid grid-cols-12 gap-3 border-t-[1px] border-[#8F95B2] pt-5 pb-3 px-3"
                    key={index}
                    ref={(el) => (containerRefs.current[index] = el)}
                >
                    <div className="col-span-12 sm:col-span-6 border-b-[1px] sm:border-b-0 border-r-0 sm:border-r-[1px] border-[#8F95B2] pb-5 sm:pb-0 mb-3 relative">
                        <div className="flex flex-col h-80 sm:h-32 relative">
                            <div className="flex items-center mb-3">
                                <Checkbox
                                    disabled={preview}
                                    checked={api.isApi}
                                    onCheckedChange={(checked) =>
                                        setApis(
                                            apis.map((api, i) =>
                                                i === index ? { ...api, isApi: checked === true } : api
                                            )
                                        )
                                    }
                                    id={"api-" + index}
                                    className="text-white mr-2"
                                />
                                <label
                                    htmlFor={"api-" + index}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white"
                                >
                                    Is api
                                </label>
                            </div>
                            <JsonInputField
                                setApis={(a) => setApis(a)}
                                index={index}
                                preview={preview}
                                api={api}
                                apis={apis}
                            />
                        </div>
                        {!preview && (
                            <Trash2
                                className="absolute top-1/2 left-[-31px] sm:left-[-30px] h-5 w-5 cursor-pointer text-white"
                                onClick={(_e) => setApis(apis.filter((_api, i) => i !== index))}
                            />
                        )}
                        <p className="absolute top-0 text-[#8F95B2] left-[-31px] sm:left-[-30px] italic inputoutput">
                            .{(index + 1).toString().padStart(2, "0")}
                        </p>
                        <p className="bg-transparent absolute bottom-[-20px] sm:bottom-1/3 right-1/2 sm:right-[-20px] font-semibold tracking-wider text-base p-2">
                            <MoveRight className="h-5 w-5 rotate-90 sm:rotate-0 text-white" />
                        </p>
                    </div>
                    <div className="col-span-12 sm:col-span-6 mb-3">
                        <div className="flex flex-col items-end">
                            {(api.output || []).map((output, io) => (
                                <div className="relative w-[100%] sm:w-[90%]" key={io}>
                                    {api.isApi ? (
                                        <CDropdown
                                            key={io}
                                            disabled={preview}
                                            items={[
                                                { title: "200" },
                                                { title: "400" },
                                                { title: "429" },
                                                { title: "404" },
                                                { title: "500" },
                                            ]}
                                            onChange={(item) =>
                                                setApis(
                                                    apis.map((api, i) =>
                                                        i === index
                                                            ? {
                                                                ...api,
                                                                output: api.output.map((out, j) =>
                                                                    j === io ? { ...out, status: item.title } : out
                                                                ),
                                                            }
                                                            : api
                                                    )
                                                )
                                            }
                                        >
                                            <p className="text-white flex justify-start items-center gap-1">
                                                {output && output.status !== "" ? output.status : "status"}
                                                <ChevronDown className="w-5 h-auto" />
                                            </p>
                                        </CDropdown>
                                    ) : (
                                        ""
                                    )}
                                    <JsonOutputField
                                        setApis={(updatedApis) => {
                                            // This wrapper function adapts the signatures
                                            setApis(updatedApis);
                                        }}
                                        index={index}
                                        preview={preview}
                                        output={{
                                            status: output.status,
                                            body: output.body,
                                            description: output.description || ""
                                        }}
                                        apis={apis}
                                        io={io}
                                    />
                                    {!preview && (
                                        <Trash2
                                            className="absolute top-1/2 right-[-31px] sm:right-[-30px] h-5 w-5 cursor-pointer text-white"
                                            onClick={(_e) =>
                                                setApis(
                                                    apis.map((api, i) =>
                                                        i === index
                                                            ? { ...api, output: api.output.filter((_out, j) => j !== io) }
                                                            : api
                                                    )
                                                )
                                            }
                                        />
                                    )}
                                </div>
                            ))}
                            {!preview && (
                                <div
                                    className="border-dashed border-2 border-white rounded-3xl flex flex-col justify-center items-center h-32 gap-3 cursor-pointer w-[100%] sm:w-[90%] mb-3 mt-[0.75rem]"
                                    onClick={(_e) =>
                                        setApis(
                                            apis.map((api, i) =>
                                                i === index
                                                    ? {
                                                        ...api,
                                                        output: [
                                                            ...(api.output || []),
                                                            { status: "", body: "", description: "" },
                                                        ],
                                                    }
                                                    : api
                                            )
                                        )
                                    }
                                >
                                    <RoundedIcon color="[#8F95B2]" height="h-11" width="w-11">
                                        <Plus className="m-auto text-[#8F95B2]" />
                                    </RoundedIcon>
                                    <p className="text-white text-center text-sm">Create a new output</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </>
    );
};

export default Apis;
